import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getUserPreferences, updateUserPreferences } from '../services/userService';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<'light' | 'dark'>('light');
  const [isLoading, setIsLoading] = useState(true);
  const { token, user } = useAuth();

  // Load theme preference from backend on mount
  useEffect(() => {
    async function loadThemePreference() {
      if (!token || !user) {
        setIsLoading(false);
        return;
      }

      try {
        const preferences = await getUserPreferences(token);
        const savedTheme = preferences?.theme as 'light' | 'dark' | undefined;

        if (savedTheme === 'light' || savedTheme === 'dark') {
          setThemeState(savedTheme);
          applyThemeToDocument(savedTheme);
        }
      } catch (error) {
        console.error('Failed to load theme preference:', error);
        // Default to light mode on error
      } finally {
        setIsLoading(false);
      }
    }

    loadThemePreference();
  }, [token, user]);

  // Apply theme class to document root
  const applyThemeToDocument = (newTheme: 'light' | 'dark') => {
    const root = document.documentElement;
    if (newTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  };

  // Set theme and persist to backend
  const setTheme = async (newTheme: 'light' | 'dark') => {
    setThemeState(newTheme);
    applyThemeToDocument(newTheme);

    // Persist to backend
    if (token) {
      try {
        await updateUserPreferences({ theme: newTheme }, token);
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  };

  // Toggle between light and dark
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
